import './paymentmethod-icon.scss';

import { TFunction } from 'i18next';

import { cn } from '@sola/ark-core/utils/helpers.js';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.js';
import { CreditCardEnum } from '@sola/schema/dotcom/checkout/billingInfo.js';
import { GraphQLSupporterPaymentMethod } from '@sola/schema/dotcom/supporters/schema.generated.js';
import { IconNames } from '@sola/schema/lib/icons/icon-names.js';

/**
 * Get payment method label.
 */
export const getLabel = (
  paymentMethod: GraphQLSupporterPaymentMethod,
  t: TFunction,
) => {
  const { creditCard, ach } = paymentMethod;
  if (creditCard) {
    return t('payments.methodLabel.card', {
      cardType: creditCard.cardType,
      lastFour: creditCard.lastFour,
    });
  }
  if (paymentMethod.ach) {
    return t('payments.methodLabel.ach', {
      lastFour: ach?.lastFourOfAccountNumber,
    });
  }
};

const ccFallbackIconName = 'credit-card-02';

/**
 * Get icon component.
 */
export const getIconName = (paymentMethod: GraphQLSupporterPaymentMethod) => {
  if (paymentMethod.creditCard?.cardType) {
    return (
      {
        [CreditCardEnum.Amex]: 'card-amex',
        'american express': 'card-amex',
        [CreditCardEnum.Discover]: 'card-discover',
        [CreditCardEnum.Mastercard]: 'card-mastercard',
        'master card': 'card-mastercard',
        [CreditCardEnum.Visa]: 'card-visa',
      }[paymentMethod.creditCard.cardType.toLowerCase()] || ccFallbackIconName
    );
  }

  if (paymentMethod.ach) {
    return 'bank';
  }

  // What else?
  return '';
};

export interface PaymentMethodIconProps {
  paymentMethod: GraphQLSupporterPaymentMethod;
}

export const PaymentMethodIcon: React.FC<PaymentMethodIconProps> = ({
  paymentMethod,
}) => {
  const iconName = getIconName(paymentMethod);
  if (!iconName) {
    return null;
  }
  return (
    <ArkIcon
      className={cn('PaymentMethodIcon__icon', {
        'PaymentMethodIcon__icon-bank rounded-sm p-snug-xl':
          !!paymentMethod?.ach,
      })}
      /* Potentially fragile, but hard to dynamically check */
      iconName={iconName as IconNames}
    />
  );
};
